import { auth } from '../config/firebase.web';
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  User,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
} from 'firebase/auth';

export interface PhoneAuthResult {
  success: boolean;
  error?: string;
  verificationId?: string;
  confirmation?: ConfirmationResult;
}

export interface VerifyOtpResult {
  success: boolean;
  error?: string;
  user?: User;
}

let recaptchaVerifier: RecaptchaVerifier | null = null;
let lastOtpRequestTime: number = 0;
const OTP_COOLDOWN_MS = 60000; // 60 seconds cooldown between requests

const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

/**
 * Tear down any existing reCAPTCHA widget.
 * Tokens are single-use; a failed sendOTP leaves a dead verifier if we keep it.
 */
const clearRecaptcha = () => {
  if (recaptchaVerifier) {
    try {
      recaptchaVerifier.clear();
    } catch {
      // Widget may already be disposed
    }
    recaptchaVerifier = null;
  }

  // Ensure the DOM container is completely removed so a new widget mounts to a fresh element
  if (typeof document !== 'undefined') {
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (container) {
      try {
        container.remove();
      } catch (err) {
        console.warn('Failed to remove reCAPTCHA container element:', err);
        container.innerHTML = '';
      }
    }
  }
};

/**
 * Ensure a single reCAPTCHA mount point exists in the DOM.
 * Prefer the static container in web/index.html; create one if missing.
 */
const ensureRecaptchaContainer = (): HTMLElement => {
  if (typeof document === 'undefined') {
    throw new Error('reCAPTCHA requires a browser environment.');
  }

  let container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = RECAPTCHA_CONTAINER_ID;
    // Keep off-screen for invisible reCAPTCHA; still in DOM for Firebase
    container.style.position = 'fixed';
    container.style.bottom = '0';
    container.style.right = '0';
    container.style.zIndex = '9999';
    document.body.appendChild(container);
  }
  return container;
};

/**
 * Create a fresh RecaptchaVerifier for each OTP send.
 * Always clear the previous one first — reusing a solved token causes
 * auth/invalid-app-credential on subsequent attempts.
 */
const initializeRecaptcha = async (): Promise<RecaptchaVerifier> => {
  clearRecaptcha();
  ensureRecaptchaContainer();

  // Invisible is preferred for UX; Firebase still falls back from Enterprise → v2
  recaptchaVerifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
    size: 'invisible',
    callback: () => {
      // Token ready; signInWithPhoneNumber consumes it
    },
    'expired-callback': () => {
      console.warn('reCAPTCHA expired — will recreate on next attempt');
      clearRecaptcha();
    },
    'error-callback': (err: unknown) => {
      console.error('reCAPTCHA error:', err);
      clearRecaptcha();
    },
  });

  // Pre-render so the first sendOTP does not race widget load
  await recaptchaVerifier.render();
  return recaptchaVerifier;
};

/**
 * Send OTP to phone number (Web)
 */
export const sendOTP = async (phoneNumber: string): Promise<PhoneAuthResult> => {
  try {
    if (!phoneNumber.startsWith('+')) {
      return {
        success: false,
        error: 'Phone number must include country code (e.g., +1234567890)',
      };
    }

    // Check cooldown period to prevent rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastOtpRequestTime;

    if (timeSinceLastRequest < OTP_COOLDOWN_MS && lastOtpRequestTime > 0) {
      const remainingSeconds = Math.ceil((OTP_COOLDOWN_MS - timeSinceLastRequest) / 1000);
      return {
        success: false,
        error: `Please wait ${remainingSeconds} seconds before requesting another OTP.`,
      };
    }

    const appVerifier = await initializeRecaptcha();

    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);

    lastOtpRequestTime = now;

    // Token is consumed — drop verifier so the next resend builds a new one
    clearRecaptcha();

    return {
      success: true,
      verificationId: confirmation.verificationId,
      confirmation,
    };
  } catch (error: any) {
    console.error('Error sending OTP:', error);
    // Always reset reCAPTCHA after failure so the next try gets a fresh token
    clearRecaptcha();

    let errorMessage = 'Failed to send OTP. Please try again.';

    if (error.code === 'auth/invalid-phone-number') {
      errorMessage = 'Invalid phone number format.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage =
        'Too many OTP requests. Please wait 10–15 minutes and try again, or use a different phone number.';
    } else if (error.code === 'auth/quota-exceeded') {
      errorMessage = 'Daily SMS quota exceeded. Please try again tomorrow or contact support.';
    } else if (error.code === 'auth/captcha-check-failed') {
      errorMessage = 'reCAPTCHA verification failed. Please try again.';
    } else if (error.code === 'auth/invalid-app-credential') {
      const origin =
        typeof window !== 'undefined' ? window.location.hostname : 'this domain';
      errorMessage =
        `Phone verification failed (invalid app credential) on "${origin}". ` +
        'In Firebase Console → Authentication → Settings → Authorized domains, add this host ' +
        '(e.g. localhost, 127.0.0.1, or your hosting domain). Also confirm Phone sign-in is enabled, ' +
        'the project is on the Blaze plan, and the Web API key is not restricted away from Identity Toolkit.';
      console.error(
        '[phoneAuth.web] auth/invalid-app-credential — checklist:\n' +
          '1. Firebase Console → Authentication → Sign-in method → Phone = Enabled\n' +
          '2. Authentication → Settings → Authorized domains includes: ' +
          origin +
          ' (and 127.0.0.1 if you use that)\n' +
          '3. Project on Blaze (pay-as-you-go) for real SMS\n' +
          '4. Google Cloud → APIs: Identity Toolkit API + reCAPTCHA Enterprise API enabled\n' +
          '5. API key restrictions allow this origin + Identity Toolkit / reCAPTCHA\n' +
          '6. App Check not enforcing Auth without a valid web provider (if enabled)'
      );
    } else if (error.code === 'auth/operation-not-allowed') {
      errorMessage =
        'Phone authentication is not enabled for this Firebase project. Enable it in Authentication → Sign-in method.';
    } else if (error.code === 'auth/billing-not-enabled') {
      errorMessage =
        'SMS phone auth requires a Blaze (pay-as-you-go) billing plan on this Firebase project.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Verify OTP code (Web)
 */
export const verifyOTP = async (
  confirmation: ConfirmationResult,
  code: string
): Promise<VerifyOtpResult> => {
  try {
    if (!code || code.length !== 6) {
      return {
        success: false,
        error: 'Please enter a valid 6-digit code.',
      };
    }

    const result = await confirmation.confirm(code);

    if (!result?.user) {
      return {
        success: false,
        error: 'Authentication failed. Please try again.',
      };
    }

    return {
      success: true,
      user: result.user,
    };
  } catch (error: any) {
    console.error('Error verifying OTP:', error);

    let errorMessage = 'Invalid verification code.';

    if (error.code === 'auth/invalid-verification-code') {
      errorMessage = 'Invalid verification code. Please try again.';
    } else if (error.code === 'auth/code-expired') {
      errorMessage = 'Verification code expired. Please request a new code.';
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
    clearRecaptcha();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

/**
 * Get current Firebase user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Get Firebase ID token for API authentication
 */
export const getIdToken = async (forceRefresh = false): Promise<string | null> => {
  try {
    const user = getCurrentUser();
    if (!user) return null;

    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChanged = (callback: (user: User | null) => void) => {
  return firebaseOnAuthStateChanged(auth, callback);
};
